import { Component, computed, input, output, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { AccordionComponent, AccordionPanelComponent } from 'ngx-bootstrap/accordion';
import { TextModel } from '../../../models/primitive/text.model';
import { safeStringify } from '@evorait/pace-components';
import { TranslateModule } from '@ngx-translate/core';
import { AddressState } from '../../../state/address/address-collection.state';

@Component({
    selector: 'pace-core-address-spector',
    imports: [CommonModule, AlertComponent, AccordionComponent, AccordionPanelComponent, TranslateModule],
    templateUrl: './address-spector.component.html',
    styleUrl: './address-spector.component.css',
})
export class AddressSpectorComponent {
    public addresses = input<AddressState[] | undefined | null>([]);
    public items: Signal<Array<{ heading: string; summary: string[]; details: { [p: string]: string } }>> = computed(
        () => {
            const addresses = this.addresses();
            if (!addresses?.length) {
                return [];
            }
            return addresses.map((addr, index) => ({
                heading: this.headingFor(addr, index),
                summary: this.summary(addr),
                details: this.details(addr),
            }));
        },
    );

    public deleteLocal = output<void>();
    public downloadJson = output<AddressState[]>();

    protected readonly preferredOrder: Array<keyof AddressState> = [
        'name1',
        'name2',
        'name3',
        'name4',
        'name1shortP58',
        'name2shortP58',
        'street',
        'postCode',
        'city',
        'state',
        'country',
        'stateOfUSA',
        'building',
        'room',
        'floor',
        'department',
        'contactName1',
        'contactName2',
        'contactType',
        'email',
        'phone',
        'contactPhone',
        'fax1',
        'fax2',
        'fax3',
        'fax4',
        'addrText',
        'addrNumber',
        'visible',
        'isFromAddressSet',
    ];

    public headingFor(addr: AddressState, index: number): string {
        const name = this.compactJoin([addr?.name1, addr?.name2]);
        if (name) {
            return this.compactJoin([name, addr?.country]);
        }

        return `Address #${index + 1}`;
    }

    public compactJoin(parts: Array<string | undefined | null>, sep = ', '): string {
        return parts
            .filter(Boolean)
            .map((p) => String(p).trim())
            .filter(Boolean)
            .join(sep);
    }

    private summary(addr: AddressState): string[] {
        if (!addr) {
            return [];
        }
        const lines: string[] = [];

        const name = this.compactJoin([addr.name1, addr.name2, addr.name3, addr.name4], ' / ');
        if (name) {
            lines.push(name);
        }

        const street = addr?.street;
        if (street) {
            lines.push(street);
        }

        const cityLine = this.compactJoin([addr.postCode, addr.city, addr.state], ' ');
        if (cityLine) {
            lines.push(cityLine);
        }

        const country = addr?.country;
        if (country) {
            lines.push(country);
        }

        const meta = this.compactJoin(
            [this.flag('visible', addr.visible), this.flag('fromSet', addr.isFromAddressSet)],
            ' // ',
        );
        if (meta) {
            lines.push(meta);
        }

        return lines;
    }

    private flag(label: string, v: unknown): string {
        return v === true ? `${label}: ✅` : v === false ? `${label}: ❌` : '';
    }

    private details(addr: AddressState): { [k: string]: string } {
        const rawEntries = Object.entries(addr ?? {});
        const rawKeys = new Set(rawEntries.map(([k]) => k));

        const details: { [k: string]: string } = {};
        for (const k of this.preferredOrder) {
            if (rawKeys.has(k)) {
                details[k] = this.prettyValue(addr[k]);
            }
        }
        return details;
    }

    private prettyValue(v: boolean | TextModel | string | number | unknown): string {
        if (v === null || v === undefined) {
            return '';
        }
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
            return String(v);
        }

        if (v instanceof TextModel) {
            return v.value;
        }

        return safeStringify(v);
    }

    public triggerDeleteLocal(): void {
        this.deleteLocal.emit();
    }

    public copyJson(): void {
        const raw = this.addresses();
        if (!raw) {
            return;
        }
        const text = JSON.stringify(raw, null, 2);
        navigator.clipboard?.writeText(text).catch(console.error);
    }

    public triggerDownloadJson(): void {
        const model = this.addresses();
        if (!model) {
            return;
        }
        this.downloadJson.emit(model);
    }
}
